import { forwardRef, memo, type FunctionComponent, type ReactNode } from 'react';
import toSafeInteger from 'lodash/toSafeInteger';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';
// Material UI imports
import { type SxProps, type Theme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
// Material Icon imports
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
// Skillmore UI Components
import { dateOnlyOptions } from '@empathco/ui-components/src/common/intl';
import { getJsDateFromISO } from '@empathco/ui-components/src/helpers/datetime';

const containerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75,
  ml: 1.25,
  mr: 2
} as const;
const rowSx: SxProps<Theme> = { display: 'flex', alignItems: 'center', gap: 1, color: 'text.label' } as const;
const labelSx: SxProps<Theme> = { flexGrow: 1, minWidth: 0 } as const;
const valueSx: SxProps<Theme> = { flexShrink: 0 } as const;

const lastLoginIcon = <CalendarTodayOutlined fontSize="small" color="inherit"/>;
const confirmedSkillsIcon = <CheckCircleOutline fontSize="small" color="inherit"/>;

type ActivityRowProps = {
  readonly icon: ReactNode;
  readonly label: string;
  readonly inactive: boolean;
  readonly children: ReactNode;
};

const ActivityRow: FunctionComponent<ActivityRowProps> = ({ icon, label, inactive, children }) => (
  <Box sx={rowSx}>
    {icon}
    <Typography variant="body2" noWrap sx={labelSx}>
      <FormattedMessage id={label}/>
    </Typography>
    <Typography variant="body2" noWrap color={inactive ? 'text.secondary' : 'text.primary'} sx={valueSx}>
      {children}
    </Typography>
  </Box>
);

type EmployeeActivityFooterProps = {
  readonly lastLoginAt?: string | null;
  readonly confirmedSkillsCount?: number | null;
};

const EmployeeActivityFooter = forwardRef<HTMLDivElement, EmployeeActivityFooterProps>(({
  lastLoginAt,
  confirmedSkillsCount
}, ref) => {
  const neverLoggedIn = !lastLoginAt;
  return (
    <Box ref={ref} sx={containerSx}>
      <Divider/>
      <ActivityRow icon={lastLoginIcon} label="team.card.last_login" inactive={neverLoggedIn}>
        {lastLoginAt ? (
          <FormattedDate
              value={getJsDateFromISO(lastLoginAt)}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...dateOnlyOptions}
          />
        ) : <FormattedMessage id="team.card.never"/>}
      </ActivityRow>
      <ActivityRow icon={confirmedSkillsIcon} label="team.card.confirmed_skills" inactive={neverLoggedIn}>
        <FormattedNumber value={toSafeInteger(confirmedSkillsCount)}/>
      </ActivityRow>
    </Box>
  );
});

EmployeeActivityFooter.displayName = 'EmployeeActivityFooter';

export default memo(EmployeeActivityFooter);
